/**
 * Tour — spotlight walkthrough over live elements (Ringo 3D pattern). The
 * callout is a bottom sheet so it works on a phone; the spotlight is a box
 * with a huge shadow. Steps whose target is not on screen are skipped. The
 * last stop offers the video for anyone who wants the longer version.
 */

import { useEffect, useLayoutEffect, useState } from 'react';
import { X, Play } from 'lucide-react';
import { useHints, TOUR_STEPS } from '../lib/hints';

function rectOf(sel) {
  const el = document.querySelector(sel);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  return r;
}

export default function Tour() {
  const h = useHints();
  const [i, setI] = useState(0);
  const [steps, setSteps] = useState([]);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!h?.tourOpen) return;
    const live = TOUR_STEPS.filter((s) => document.querySelector(s.target));
    setSteps(live);
    setI(0);
  }, [h?.tourOpen]);

  useLayoutEffect(() => {
    if (!h?.tourOpen || !steps[i]) return undefined;
    const el = document.querySelector(steps[i].target);
    // Tall targets (the Deep Rest card) go to the top of the viewport so the
    // callout below leaves them visible; everything else is centered.
    const tall = el && el.getBoundingClientRect().height > window.innerHeight * 0.4;
    const fixed = el && getComputedStyle(el).position === 'fixed';
    if (el && !fixed) el.scrollIntoView({ block: tall ? 'start' : 'center', behavior: 'smooth' });
    const measure = () => setRect(rectOf(steps[i].target));
    const t = setTimeout(measure, 350);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [h?.tourOpen, steps, i]);

  if (!h?.tourOpen || !steps.length) return null;
  const step = steps[i];
  const last = i === steps.length - 1;
  const pad = 8;
  // Put the callout on whichever side leaves the spotlight uncovered: targets
  // in the lower half (the Deep Rest card, the tab bar) get it at the top.
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const atTop = !!rect && (rect.top + rect.height / 2) > vh * 0.5;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Tour">
      {/* Spotlight: the box itself is transparent; its shadow dims everything else */}
      {rect ? (
        <div
          className="absolute rounded-xl transition-all duration-300"
          style={{ left: rect.left - pad, top: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
                   boxShadow: '0 0 0 9999px rgba(2, 6, 23, 0.72)', border: '2px solid rgba(129,140,248,0.9)' }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-950/70" />
      )}
      <div
        className={`absolute inset-x-0 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-[28rem] p-4 ${atTop ? 'top-0 sm:top-6' : 'bottom-0 sm:bottom-6'}`}
        style={atTop ? { paddingTop: 'max(1rem, env(safe-area-inset-top))' } : { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="rounded-2xl border border-indigo-800/60 bg-slate-900 shadow-xl p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-indigo-300 mb-1">{i + 1} of {steps.length}</p>
              <h3 className="text-base font-semibold text-slate-100">{step.title}</h3>
            </div>
            <button onClick={() => h.endTour(false)} aria-label="Skip tour" className="text-slate-500 hover:text-slate-300 p-1 -m-1"><X size={16} /></button>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed mt-2">{step.text}</p>
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-1.5" aria-hidden>
              {steps.map((_, k) => <span key={k} className={`h-1.5 rounded-full ${k === i ? 'w-4 bg-indigo-400' : 'w-1.5 bg-slate-700'}`} />)}
            </div>
            <div className="flex items-center gap-2">
              {last && (
                <button onClick={() => { h.endTour(true); h.openVideo(); }} className="text-xs px-3 py-2 rounded-md border border-indigo-700/60 text-indigo-200 hover:bg-indigo-900/40 flex items-center gap-1.5">
                  <Play size={12} /> Watch the walkthrough (2½ min)
                </button>
              )}
              <button onClick={() => (last ? h.endTour(true) : setI(i + 1))} className="text-xs px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white font-medium">
                {last ? 'Done' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
